<template>
  <TransitionFade>
    <div
      v-if="message"
      class="ModalMessageComponent"
    >
      <i :class="icon" />

      <h1>
        {{ te(message.title) ? t(message.title) : message.title }}
      </h1>

      <p>
        {{ bodyText }}
      </p>

      <p>
        {{ hiringNumberText }}
      </p>

      <div v-if="message.extra && message.extra.hiringNumber">
        <div class="request_number">
          <span> {{ message.extra.hiringNumber }}</span>
        </div>
      </div>

      <p v-if="message.extra && message.extra.mailReminderAfterEmission">
        {{ message.extra.mailReminderAfterEmission }}
      </p>

      <BaseButton
        v-for="(button, i) in message.buttons"
        :key="i"
        :type="button.type"
        :icon="button.icon"
        @click="onClick(button)"
      >
        {{ te(button.text) ? t(button.text) : button.text }}
      </BaseButton>
    </div>
  </TransitionFade>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import { useNavigation } from '@/modules/core/composables/navigationComposable'
  import type Message from '@/modules/core/models/modalMessage/Message'
  import type ModalButton from '@/modules/core/models/modalMessage/ModalButton'
  import { MessageEnum } from '../../models/enums'
  import TransitionFade from '@/modules/core/components/transitions/TransitionFade.vue'

  const router = useRouter()
  const { replacePreservingMCode } = useNavigation()
  const { t, te } = useI18n()

  interface PropsInterface {
    message: Message | null
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})
  const emit = defineEmits(['clear'])

  const icon = computed(() => {
    if (props.message?.type === MessageEnum.ERROR) {
      return 'icon-incorrecto'
    }
    if (props.message?.type === MessageEnum.PENDING) {
      return 'icon-reloj-v1'
    }
    if (props.message?.type === MessageEnum.SUPPORT) {
      return 'icon-atencion-al-cliente'
    }

    return 'icon-correcto'
  })

  const hasHiringNumber = computed(() => {
    const { message } = props
    if (!message) return false

    return message.extra && message.extra.hiringNumber
  })

  const bodyText = computed(() => {
    const { message } = props
    if (!message) return ''

    const mainText = te(message.label) ? t(message.label) : message.label

    return `${mainText}`
  })

  const hiringNumberText = computed(() =>
    hasHiringNumber.value ? t('common.error.hiring-number') : ''
  )

  async function onClick(button: ModalButton) {
    if (button.callback) {
      button.callback()
    }

    if (button.navTo) {
      replacePreservingMCode(router, { name: button.navTo })
    }

    if (button.onclick) {
      button.onclick()
      return
    }

    emit('clear')
  }
</script>

<style lang="scss" scoped>
  .ModalMessageComponent {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $gap-xl;
    justify-content: center;
    position: relative;
    inset: 0;
    padding: $spacing-2xl $spacing-l;
    margin: 0 $spacing-negative-l $spacing-negative-s;
    background-color: $color-neutral-00;
    height: $size-full;
    z-index: $z-index-20;

    @include breakpoint-md {
      margin-top: $spacing-negative-s;
      margin-bottom: 0;
    }

    .request_number {
      text-align: center;
      padding: $spacing-xl 0 $spacing-l;

      span {
        background-color: $color-blue-200;
        color: $color-neutral-dark;
        padding: $spacing-s $spacing-xl;
        font-weight: $font-weight-700;
        border-radius: $b-radius-50;
      }
    }

    i {
      font-size: $font-size-60;
      width: $size-60;
      height: $size-60;
      margin-bottom: 26px;

      @include breakpoint-md {
        font-size: $font-size-80;
        width: $size-80;
        height: $size-80;
      }

      &.icon-incorrecto {
        &::before {
          color: $color-error;
        }
      }

      &.icon-correcto {
        &::before {
          color: $color-primary;
        }
      }
    }

    h1 {
      color: $color-primary-dark;
      font-size: $font-size-2xl;
      font-weight: $font-weight-500;
      line-height: $line-height-130p;
      max-width: $size-400;
      text-align: center;

      @include breakpoint-md {
        font-size: $font-size-40;
      }
    }

    p {
      color: $color-primary-dark;
      max-width: $size-400;
      text-align: center;
      font-size: $font-size-l;
    }
  }
</style>
